import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import bestFiveClue from '@sorare/core/src/assets/crafting-clues/best-five.webp';
import bestStarRankClue from '@sorare/core/src/assets/crafting-clues/best-star-rank.webp';
import competitionClue from '@sorare/core/src/assets/crafting-clues/competition.webp';
import divisionClue from '@sorare/core/src/assets/crafting-clues/division.webp';
import countryClue from '@sorare/core/src/assets/crafting-clues/nationality.webp';
import positionClue from '@sorare/core/src/assets/crafting-clues/position.webp';
import shuffleClue from '@sorare/core/src/assets/crafting-clues/shuffle.webp';
import { Amount } from '@sorare/core/src/atoms/Amount';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, marketCredits, wheel } from '@sorare/core/src/lib/glossary';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';
import { getRewardImageUrl, isXPCurrency } from '@sorare/core/src/lib/rewards';

import { getXpIllustration } from 'lib/wheelReward';

import type { useWheelReward_probabilisticBundleSlotConfig } from './__generated__/utils.graphql';
import jerseyUrl from './assets/jersey.webp';
import nothingUrl from './assets/nothing.svg';
import respinUrl from './assets/respin.svg';
import ticketUrl from './assets/ticket.webp';

type WheelReward = {
  illustration: string;
  description: ReactNode;
  longDescription?: ReactNode;
  amount?: ReactNode;
  rewardName?: ReactNode;
};

const PlayerImageWrapper = styled(ResponsiveImg)`
  border: 1px solid var(--c-nd-300);
  background-color: var(--c-nl-500);
  border-radius: 50%;
  aspect-ratio: 1;
  width: 40%;
  object-fit: contain;
`;

export const useWheelReward = withFragments(
  (config: useWheelReward_probabilisticBundleSlotConfig): WheelReward => {
    const { toMonetaryAmount } = useMonetaryAmount();
    const { formatMessage } = useIntl();

    // jersey
    // ticket
    if (config.deliverableItems?.length) {
      const { deliverableItem } = config.deliverableItems[0];

      if (isType(deliverableItem, 'JerseyDeliverableItem')) {
        return {
          illustration: deliverableItem.pictureUrl || jerseyUrl,
          description:
            deliverableItem.name ||
            (deliverableItem.signed
              ? formatMessage({
                  id: 'wheel.slot.deliverableItem.signed',
                  defaultMessage: 'SIGNED JERSEY',
                })
              : formatMessage({
                  id: 'wheel.slot.deliverableItem',
                  defaultMessage: 'JERSEY',
                })),
        };
      }
      if (isType(deliverableItem, 'TicketsDeliverableItem')) {
        return {
          illustration: ticketUrl,
          description: formatMessage({
            id: 'wheel.slot.deliverableItem.ticket',
            defaultMessage: 'TICKET',
          }),
        };
      }
    }

    // card
    if (config.cards?.length) {
      const card = config.cards[0];
      const cardPlayers = card.players || [];

      const cardQuality = card.quality
        ? qualityNames[card.quality]
        : formatMessage(glossary.card);

      const description =
        cardPlayers.length === 1
          ? cardPlayers[0]?.abbreviatedName
          : cardQuality;

      return {
        illustration: getRewardImageUrl('card', card.rarity),

        // hack to display the player underneath the illustration
        description: (
          <Vertical>
            {cardPlayers.length > 0 && (
              <Horizontal center>
                {cardPlayers.map(({ slug, avatarPictureUrl }) => (
                  <PlayerImageWrapper
                    key={slug}
                    src={avatarPictureUrl}
                    alt=""
                    width={60}
                    cropWidth={80}
                  />
                ))}
              </Horizontal>
            )}
            {description}
          </Vertical>
        ),
        longDescription: cardQuality,
        rewardName: cardQuality,
      };
    }

    // card shard
    if (config.cardShards?.length) {
      const cardShard = config.cardShards[0];

      return {
        illustration: getRewardImageUrl('shard', cardShard.rarity),
        description: `${cardShard.quantity}`,
        longDescription: formatMessage(glossary.essenceRarityAmountLabel, {
          amount: cardShard.quantity,
          rarity: cardShard.rarity,
        }),
        rewardName: formatMessage(glossary.essence),
        amount: cardShard.quantity,
      };
    }

    // conversion credit
    if (config.conversionCredit) {
      return {
        illustration: 'https://frontend-assets.sorare.com/rewards/credit.png',
        description: (
          <Vertical>
            <Amount
              monetaryAmount={toMonetaryAmount(
                config.conversionCredit.maxDiscount
              )}
            />

            <BrandXS color="var(--c-nd-500)">
              <FormattedNumber
                value={config.conversionCredit.percentageDiscount}
                style="percent"
              />
            </BrandXS>
          </Vertical>
        ),
        longDescription: formatMessage(marketCredits.description, {
          percentage: config.conversionCredit.percentageDiscount,
          maxDiscount: (
            <Amount
              monetaryAmount={toMonetaryAmount(
                config.conversionCredit.maxDiscount
              )}
            />
          ),
        }),
        amount: (
          <Amount
            monetaryAmount={toMonetaryAmount(
              config.conversionCredit.maxDiscount
            )}
          />
        ),
        rewardName: formatMessage(marketCredits.marketCredits),
      };
    }

    if (config.inGameCurrencies?.length) {
      const igc = config.inGameCurrencies.filter(
        x => x.currency !== InGameCurrency.GOLDEN_WHEEL_TICKET
      )[0];

      if (igc) {
        const data: Record<
          | 'DIVISION_CRAFT_CLUE'
          | 'POSITION_CRAFT_CLUE'
          | 'COMPETITION_CRAFT_CLUE'
          | 'COUNTRY_CRAFT_CLUE'
          | 'SPECIAL_EVENT_CRAFT_CLUE'
          | 'FIFTY_FIFTY_CRAFT_CLUE'
          | 'BEST_STAR_RANK_CRAFT_CLUE',
          { picture: string; name: MessageDescriptor }
        > = {
          [InGameCurrency.DIVISION_CRAFT_CLUE]: {
            picture: divisionClue,
            name: wheel.divisionClue,
          },
          [InGameCurrency.POSITION_CRAFT_CLUE]: {
            picture: positionClue,
            name: wheel.positionClue,
          },
          [InGameCurrency.COMPETITION_CRAFT_CLUE]: {
            picture: competitionClue,
            name: wheel.competitionClue,
          },
          [InGameCurrency.COUNTRY_CRAFT_CLUE]: {
            picture: countryClue,
            name: wheel.countryClue,
          },
          [InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE]: {
            picture: shuffleClue,
            name: wheel.shuffleClue,
          },
          [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: {
            picture: bestFiveClue,
            name: wheel.bestFiveClue,
          },
          [InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE]: {
            picture: bestStarRankClue,
            name: wheel.bestStarRankClue,
          },
        };
        if (igc.currency in data) {
          const d = data[igc.currency as keyof typeof data];
          return {
            illustration: d.picture,
            description: (
              <>
                {igc.amount} {formatMessage(d.name)}
              </>
            ),
          };
        }

        // XP
        if (isXPCurrency(igc?.currency)) {
          const xp = igc.amount;
          return {
            illustration:
              igc.currency === InGameCurrency.COMMON_XP
                ? // TODO: support common xp low, medium, high assets
                  getRewardImageUrl('xp', Rarity.common)
                : getXpIllustration(xp),
            description: (
              <>
                {igc.amount} {formatMessage(glossary.xp)}
              </>
            ),
            amount: (
              <>
                {igc.amount} {formatMessage(glossary.xp)}
              </>
            ),
            rewardName: ' ',
          };
        }

        // respin
        if (igc.currency === InGameCurrency.WHEEL_TICKET) {
          return {
            illustration: respinUrl,
            description: formatMessage(wheel.spinAgain),
          };
        }
      }
    }

    return {
      // FIXME
      illustration: nothingUrl,
      description: formatMessage(wheel.nothing),
    };
  },
  {
    probabilisticBundleSlotConfig: gql`
      fragment useWheelReward_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
        id
        deliverableItems {
          quantity
          deliverableItem {
            slug
            pictureUrl
            name
            ... on JerseyDeliverableItem {
              slug
              signed
            }
          }
        }
        cards {
          quality
          rarity
          players {
            slug
            avatarPictureUrl
            abbreviatedName
          }
        }
        rewardShopItems {
          quantity
          shopItem {
            id
            position
            name
          }
        }
        conversionCredit {
          id
          maxDiscount {
            ...useMonetaryAmount_monetaryAmount
          }
          percentageDiscount
        }
        inGameCurrencies {
          id
          amount
          currency
        }
        cardShards {
          id
          quantity
          rarity
        }
      }

      ${useMonetaryAmount.fragments.monetaryAmount}
    ` as TypedDocumentNode<useWheelReward_probabilisticBundleSlotConfig>,
  }
);
